import React, { useEffect, useState } from 'react';
import { useGamingStore } from '../../store/gamingStore';
import { ArrowLeft, Search, Filter, SlidersHorizontal } from 'lucide-react';
import GameCard from './GameCard';

const GENRES = ['Tous', 'Survival', 'FPS', 'RPG', 'Simulation', 'Action', 'Racing', 'Strategy', 'Puzzle', 'Horror'];
const SORT_OPTIONS = [
  { value: '', label: 'Pertinence' },
  { value: 'popular', label: 'Populaire' },
  { value: 'rating', label: 'Mieux notés' },
  { value: 'newest', label: 'Récents' },
  { value: 'price_asc', label: 'Prix croissant' },
  { value: 'price_desc', label: 'Prix décroissant' },
];

const GameStore: React.FC = () => {
  const { storeGames, libraryGames, isLoadingStore, fetchStoreGames, goBackToHub } = useGamingStore();
  const [search, setSearch] = useState('');
  const [genre, setGenre] = useState('');
  const [sort, setSort] = useState('');

  useEffect(() => {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (genre) params.genre = genre;
    if (sort) params.sort = sort;
    fetchStoreGames(params);
  }, [genre, sort, fetchStoreGames]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (genre) params.genre = genre;
    if (sort) params.sort = sort;
    fetchStoreGames(params);
  };

  return (
    <div className="h-full overflow-y-auto gaming-scrollbar">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={goBackToHub}
            className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Hub
          </button>
          <h1 className="text-2xl font-black text-text-primary">Store</h1>
        </div>

        {/* Filters bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px] max-w-sm relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un jeu..."
              className="w-full bg-bg-tertiary border border-border-color rounded-lg pl-9 pr-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gaming-accent/50 transition-colors"
            />
          </form>

          <div className="flex items-center gap-2 flex-wrap">
            {GENRES.map(g => (
              <button
                key={g}
                onClick={() => setGenre(g === 'Tous' ? '' : g)}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                  (g === 'Tous' && !genre) || genre === g
                    ? 'bg-gaming-accent/20 border-gaming-accent/40 text-gaming-accent font-semibold'
                    : 'bg-bg-tertiary border-border-color text-text-secondary hover:border-gaming-accent/30'
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-bg-tertiary border border-border-color rounded-lg px-3 py-1.5 text-xs text-text-secondary focus:outline-none focus:border-gaming-accent/50"
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Games grid */}
        {isLoadingStore ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="aspect-[2/3] rounded-lg bg-bg-tertiary animate-pulse" />
            ))}
          </div>
        ) : storeGames.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-text-muted text-sm">Aucun jeu trouvé</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {storeGames.map(game => (
              <GameCard key={game.id} game={game} owned={libraryGames.some(g => g.id === game.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GameStore;
